import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { BrandMark } from '@/components/BrandMark';
import { DirectoryModal } from '@/components/DirectoryModal';
import { HiworksMailButton } from '@/components/HiworksMailButton';
import { MenuFavoriteStar } from '@/components/MenuFavoriteStar';
import { MenuIcon } from '@/components/MenuIcon';
import { NewBadge } from '@/components/NewBadge';
import { ReleaseNotesModal } from '@/components/ReleaseNotesModal';
import { apiFetch } from '@/lib/api/client';
import { resolveMenuCapability, useMenuCapability } from '@/lib/auth/permissions';
import { useLogout, useSession } from '@/lib/auth/session';
import { FEATURE_SEARCH_ENTRIES, FORM_SEARCH_ALIASES, scoreFeatureHit, stemOfRequest } from '@/lib/menu/featureSearch';
import { iconNameOf } from '@/lib/menu/icons';
import { type MenuConfigItem } from '@/lib/menu/menuConfig';
import { useMenuFavoriteActions, useMenuFavorites } from '@/lib/menu/useMenuFavorites';
import { landingHrefOf } from '@/lib/menu/useMenuSection';
import { useClosedMenuKeys } from '@/lib/menu/useMenuGate';
import { useMenuTree } from '@/lib/menu/useMenuTree';
import { downloadActiveProgram, useActiveProgramDownload } from '@/lib/programDownload';
import { useShortcutLinks } from '@/lib/shortcuts/useShortcuts';
import { isAdministrator, isSuperAdmin } from '@/lib/types';
import { normalizeSearch } from '@/lib/text/normalizeSearch';
import { useAppVersion } from '@/lib/version';
import { groupForms, isFormEnabled } from '@/pages/approval/formGroups';
import { useApprovalForms } from '@/pages/approval/useApproval';

/** 사이드바 첫 줄이 가리키는 곳. {@link AppLayout} 의 같은 상수와 짝이다. */
const HOME_PATH = '/home';

/** 사이드바 검색 결과 한 줄 — 메뉴 줄이거나, 화면 안쪽 기능 줄(`featureSearch` · 결재 서식)이다. */
type SearchHit =
  | { kind: 'menu'; item: MenuConfigItem; groupLabel?: string; score: number }
  | { kind: 'feature'; key: string; label: string; trail: string; href: string; icon: string; score: number };

/**
 * 이 메뉴가 검색어에 얼마나 맞는가 — <b>0 이면 안 걸린 것</b>이다.
 *
 * <h2>왜 점수를 매기나</h2>
 * 걸러내기만 하면 결과가 <b>트리 순서</b>로 나온다. 「권한」 을 치면 사이드바에서 위에 있는 줄부터
 * 나오는데, 정작 찾던 「권한 진단」 은 셋째 줄에 묻힌다. 사람은 친 말과 <b>가장 가까운 이름</b>이
 * 맨 위에 있기를 기대한다.
 *
 * <p>점수는 <b>가까운 순</b>이다 — 이름이 통째로 같다 → 이름이 그 말로 시작한다 → 이름 안에 있다 →
 * 별칭 → 상위 메뉴 이름. 같은 점수끼리는 트리 순서가 그대로 남는다(안정 정렬).
 */
function scoreMenuHit(
  item: MenuConfigItem,
  groupLabel: string | undefined,
  query: string,
  closedMenuKeys: ReadonlySet<string>,
): number {
  const label = normalizeSearch(item.label);
  if (label === query) return 100;
  if (label.startsWith(query)) return 80;
  if (label.includes(query)) return 60;

  // v1 이름('주간회의' …)으로 찾는 사람이 가장 헤매는 사람이다.
  if ((item.searchAliases ?? []).some((alias) => normalizeSearch(alias).includes(query))) return 40;

  /*
    한 화면 안의 보기·탭 이름(전술판·조직도). <b>그 자리가 열려 있을 때만</b> 걸린다
    (사용자 지적 2026-08-28). 트리는 이미 게이트가 걸려 있지만 별칭은 그 규칙을 우회한다 —
    닫아 둔 보기 이름으로 열린 부모 줄이 나오면 「있는데 못 쓰는 기능」이 된다.
  */
  if (
    (item.gatedSearchAliases ?? []).some(
      ({ alias, gateKey }) => !closedMenuKeys.has(gateKey) && normalizeSearch(alias).includes(query),
    )
  ) {
    return 40;
  }

  // 「숙소신청」 · 「자재신청」 처럼 일 이름 뒤에 「신청」 을 붙여 친 말(사용자 지적 2026-10-22). 별칭보다 약하다.
  const stem = stemOfRequest(query);
  if (
    stem &&
    (label.includes(stem) || (item.searchAliases ?? []).some((alias) => normalizeSearch(alias).includes(stem)))
  ) {
    return 30;
  }

  // 상위 메뉴 이름으로도 걸린다 — 「설정」 을 치면 그 아래 화면들이 함께 나온다. 가장 약한 근거라
  // 맨 아래에 둔다: 그룹 이름 하나로 스무 줄이 함께 뜨는 일이 있기 때문이다.
  if (groupLabel && normalizeSearch(groupLabel).includes(query)) return 20;

  return 0;
}

/**
 * 서랍 맨 아래 버전 줄. 손으로 적던 `v2.0.0` 을 <b>배포된 이미지 번호</b>로 바꾼 자리다 —
 * 문의가 오면 이 한 줄만 보고 어느 빌드인지 안다({@link useAppVersion}).
 *
 * <h2>누르면 무엇이 바뀌었는지 나온다</h2>
 * 번호만으로는 「어제와 뭐가 다른가」 를 알 수 없다. 사람이 그것을 물을 자리는 <b>번호 자신</b>이라,
 * 메뉴를 새로 만들지 않고 이 줄을 누르는 자리로 둔다({@link ReleaseNotesModal}).
 *
 * <h2>최고 관리자는 변경이력 화면으로 간다 (사용자 지시 2026-10-14)</h2>
 * 노트 창과 환경설정 › 변경이력은 같은 노트를 같은 묶음(날짜별·Job 버전별)으로 보여 준다. 고칠 수
 * 있는 사람은 창 대신 고치는 화면으로 바로 보낸다. 그 화면은 최고 관리자 전용이라, 나머지는 지금처럼
 * 창이 뜬다 — 사용자가 정한 갈림이다.
 */
function VersionLine({ onNavigate }: { onNavigate: () => void }) {
  const version = useAppVersion();
  const [notesOpen, setNotesOpen] = useState(false);
  const { data: session } = useSession();
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin(session?.user?.userType);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (!superAdmin) {
            setNotesOpen(true);
            return;
          }
          navigate('/settings/release-notes');
          onNavigate();
        }}
        title="릴리즈 노트"
        className="px-4 pb-3 text-left hover:underline"
        style={{
          display: 'block',
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-muted)',
          fontSize: '7pt',
        }}
      >
        {version}
      </button>
      {notesOpen && (
        <ReleaseNotesModal
          onClose={() => setNotesOpen(false)}
          onNavigate={() => {
            setNotesOpen(false);
            // 같은 경로 안의 탭으로 가는 줄은 경로가 안 바뀌어 서랍이 스스로 닫히지 않는다.
            onNavigate();
          }}
        />
      )}
    </>
  );
}

/** 사이드바 결재 대기 건수 배지(빨간 알약). */
function CountBadge({ n }: { n: number }) {
  return (
    <span
      style={{
        marginLeft: 'auto',
        fontSize: '10px',
        fontWeight: 700,
        color: '#fff',
        background: '#dc2626',
        borderRadius: '9px',
        padding: '1px 6px',
        lineHeight: 1.5,
      }}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

/**
 * 즐겨찾기 칸의 한 줄 — 이동은 이름이, 차례 바꾸기와 빼기는 오른쪽 손잡이가 맡는다 (V325).
 *
 * <p>끌어다 놓기(drag)를 쓰지 않았다. 사이드바는 좁고 줄 높이가 32px 이라 잡는 자리가 작고,
 * 모바일 서랍에서는 스크롤과 다툰다. ↑↓ 는 눌린 줄이 어디로 갔는지 눈으로 따라갈 수 있다.
 *
 * <h2>어디에 있던 줄인지 함께 적는다 (사용자 지시 2026-09-30)</h2>
 * 별이 하위 탭까지 붙은 뒤로 이 칸에는 <b>깊은 곳의 이름</b>이 섞여 선다 — 「연차 촉진」·「현황」
 * 처럼 그 자체로는 어느 묶음의 것인지 알 수 없는 이름들이다. 그래서 위에 <b>거쳐 온 길</b>을
 * 옅게 한 줄 적는다(「인사관리 › 인사자료」).
 *
 * <p>한 줄로 이어 쓰지 않는다 — 사이드바 폭이 208px 이라 「인사관리 › 인사자료 › 연차 촉진」 은
 * 잘리고, 잘리는 쪽이 <b>정작 눌러야 할 끝 단</b>이다. 길은 위에, 이름은 아래에 온전히 둔다.
 */
/** 하위 메뉴 판의 연결선 색과, 칸(테두리 1 + py-1.5 + text-sm 20 + 테두리 1 = 34px)의 세로 가운데. */
const FLYOUT_LINE = '#cbd5e1';
const FLYOUT_ROW_MID = 17;

function FavoriteRow({
  item,
  href,
  trail,
  active,
  first,
  last,
  onOpen,
  onUp,
  onDown,
  onRemove,
}: {
  item: MenuConfigItem;
  /**
   * 갈 곳. <b>{@code item.href} 를 쓰지 않는다</b> — 그룹(인사관리·환경설정)은 그 칸이 비어 있어
   * 예전에는 `#` 로 떨어졌다. 부르는 쪽이 첫 자식까지 풀어 넘긴다({@code landingHrefOf}).
   */
  href: string;
  /** 거쳐 온 묶음 이름들(위→아래). 맨 윗줄 메뉴를 담았으면 비어 있다. */
  trail: string[];
  active: boolean;
  first: boolean;
  last: boolean;
  onOpen: () => void;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) {
  const path = [...trail, item.label].join(' › ');

  return (
    <div className="group flex items-center">
      <Link
        to={href}
        onClick={onOpen}
        title={path}
        className="flex items-center gap-2.5 py-2 rounded-lg text-sm transition-all"
        style={{
          flex: 1,
          minWidth: 0,
          paddingLeft: '12px',
          background: active ? '#eff6ff' : 'transparent',
          color: active ? 'var(--accent)' : 'var(--text-secondary)',
          fontWeight: active ? 600 : 400,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2" style={{ flexShrink: 0 }}>
          <path d="M12 3l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.9l1.1-6.5L2.6 9.8l6.5-.9z" />
        </svg>
        <span style={{ minWidth: 0 }}>
          {trail.length > 0 && (
            <span
              className="block truncate"
              style={{ fontSize: '10px', lineHeight: 1.3, color: 'var(--text-muted)', fontWeight: 400 }}
            >
              {trail.join(' › ')}
            </span>
          )}
          <span className="block truncate">{item.label}</span>
        </span>
      </Link>
      {/* 손잡이는 마우스를 올렸을 때만. 평소에는 이름만 보이는 목록이어야 한다. */}
      <span className="flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100">
        <FavoriteHandle label={`${item.label} 위로`} disabled={first} onClick={onUp}>↑</FavoriteHandle>
        <FavoriteHandle label={`${item.label} 아래로`} disabled={last} onClick={onDown}>↓</FavoriteHandle>
        <FavoriteHandle label={`${item.label} 즐겨찾기 빼기`} onClick={onRemove}>✕</FavoriteHandle>
      </span>
    </div>
  );
}

function FavoriteHandle({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="rounded hover:bg-slate-100"
      style={{
        padding: '2px 5px',
        fontSize: '11px',
        lineHeight: 1.2,
        color: 'var(--text-muted)',
        opacity: disabled ? 0.3 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * 상단 헤더 + 좌측 사이드바.
 *
 * v1 `components/Navigation.tsx` 를 옮긴 것이다. 화면은 같고 데이터 경로가 바뀌었다.
 *  - `usePathname` → `useLocation`
 *  - `next/link` → React Router `Link`
 *  - supabase 직접 조회 13건 → 백엔드 API 2건 (메뉴 설정 / 메뉴 활동)
 *  - 손수 만든 세션 스토어 → `useSession`
 *  - `router.prefetch` 제거 — SPA 는 라우트가 이미 번들에 있어 미리 받아올 것이 없다.
 *    (코드 분할을 도입하면 그때 라우트 lazy import 의 preload 로 되살린다.)
 *
 * 메뉴 트리 계산은 {@link useMenuTree} 가 한다 — 홈 위젯이 같은 트리를 그리기 때문이다.
 *
 * 여기저기 붙은 `data-tour` 는 안내 투어가 하이라이트할 자리를 가리키는 이름표다
 * ({@code lib/tour/steps.ts}). 클래스나 DOM 구조로 찾게 두면 스타일을 손볼 때 안내가 조용히 깨진다.
 */
export function Navigation({
  showSidebar = true,
  collapsed = false,
  onToggleCollapsed,
  onStartTour,
  storageDockAvailable = false,
  storageDockOpen = false,
  onToggleStorageDock,
}: {
  showSidebar?: boolean;
  /** 데스크톱에서 사이드바를 접었는지. 상태는 {@link AppLayout} 이 쥔다(본문 여백과 함께 움직여야 하므로). */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** 안내 투어 열기. 투어도 상태를 {@link AppLayout} 이 쥔다 — 헤더·사이드바·홈을 함께 가리키므로. */
  onStartTour?: () => void;
  /*
    AI 도크를 여는 손은 여기 없다 — 오른쪽 아래에 늘 떠 있는 동그라미가 맡는다
    ({@link AiAgentBubble}, 사용자 지시 2026-09-17). 헤더는 폭이 정해져 있어 바로가기·직원찾기·?·
    개선사항 요청과 다퉜고, 좁은 화면에서는 이름이 잘렸다.
  */
  /**
   * 이 화면에서 자료실 도크를 열 수 있는가. 거짓이면 「자료실」 버튼이 자료실 화면으로 간다 —
   * 좁은 화면과 자료실 화면 자신이 그렇다({@link AppLayout} 의 `storageDockAvailable`).
   */
  storageDockAvailable?: boolean;
  storageDockOpen?: boolean;
  onToggleStorageDock?: () => void;
}) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { topLevel, childrenOf, hasBadge, isActive } = useMenuTree();
  /**
   * 사이드바 즐겨찾기(V325 · 사용자 지시 2026-09-22 「메뉴 순서를 바꾸게 하거나 즐겨찾기」).
   *
   * <p>본 메뉴의 차례는 건드리지 않는다 — 그 자리는 이미 골격·관리자 설정·소속이 함께 정하고
   * 있어서, 개인 순서를 그 위에 얹으면 넷이 부딪힌다. 대신 <b>맨 위에 제 칸</b>을 두고 그 안에서만
   * 사람이 차례를 매긴다.
   */
  const { data: favoriteKeys = [] } = useMenuFavorites();
  const favoriteActions = useMenuFavoriteActions();
  const favoriteSet = useMemo(() => new Set(favoriteKeys), [favoriteKeys]);
  // 아직 열지 않은 것(오픈 게이트 · V153). 헤더 버튼은 메뉴가 아니라 메뉴 트리에서 빠지지 않으므로
  // 여기서 직접 본다. 관리자에게는 빈 집합이라 예전과 똑같이 보인다.
  const closedMenuKeys = useClosedMenuKeys();
  const logout = useLogout();

  /*
    전자결재를 <b>쓸 수 있는 사람인가</b> — 헤더 버튼과 그 옆 건수를 이 값이 정한다.

    <p>예전에는 <b>사이드바 트리에 그 줄이 있는가</b>로 봤다. 2026-09-22 에 사이드바 줄을 걷고
    헤더 버튼 하나로 통일하면서(사용자 지시) 그 방법이 더는 통하지 않는다 — 트리에 없는 것이
    「권한이 없다」 는 뜻이 아니게 됐다. 그래서 <b>권한과 게이트를 직접</b> 본다:

    <pre>
      권한   menuPermissions 의 approval.canView (관리자는 전부 참)
      게이트 아직 열지 않은 키면 닫는다 — 사이드바가 걷어 주던 것을 여기서 한다
    </pre>

    이 조건이 거짓이면 대기 건수도 부르지 않는다(403 방지).
  */
  const canApproval = useMenuCapability('approval', 'view');
  const hasApprovalMenu = canApproval && !closedMenuKeys.has('approval');
  const approvalInbox = useQuery({
    queryKey: ['approval', 'box', 'count'],
    queryFn: () => apiFetch<number>('/v1/approvals/inbox-count'),
    enabled: hasApprovalMenu,
  });
  const approvalCount = approvalInbox.data ?? 0;

  /*
    프로그램 다운로드 — <b>기간 안에만 서는</b> 설치 파일 받기 버튼(V397 · 사용자 지시 2026-10-02).
    환경설정 › 메뉴 관리에서 파일과 기간을 등록하면 그 기간 동안 관리자 대시보드 옆에 선다.
    로그인한 사람이면 누구나 본다(권한 축이 아니다) — 서비스가 바뀌어 전 직원이 받아야 하는 파일이다.
    서명된 주소는 누를 때 받는다 — 응답에 실어 두면 화면 캐시보다 먼저 만료된다.
  */
  const programDownload = useActiveProgramDownload(Boolean(session?.user));
  const activeProgram = programDownload.data?.available ? programDownload.data : null;
  const [programReceiving, setProgramReceiving] = useState(false);
  const receiveProgram = () => {
    setProgramReceiving(true);
    downloadActiveProgram()
      .catch((cause: unknown) => {
        window.alert(cause instanceof Error ? cause.message : '파일을 받지 못했습니다.');
      })
      .finally(() => setProgramReceiving(false));
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState('');
  const [showDirectoryModal, setShowDirectoryModal] = useState(false);
  /*
    윈도우 검색에서 온 사람 — PC Off 트레이가 시작 메뉴에 깔아 둔 직원 바로가기가 `?person=<id>` 로 들어온다
    (사용자 지시 2026-10-22). 직원찾기를 그 사람을 고른 채로 열고 주소에서는 떼어 낸다 — 남겨 두면
    새로 고침할 때마다 다시 열린다.
  */
  const [directoryPersonId, setDirectoryPersonId] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(search);
    const person = params.get('person');
    if (!person) return;
    setDirectoryPersonId(person);
    setShowDirectoryModal(true);
    params.delete('person');
    const rest = params.toString();
    navigate({ pathname, search: rest ? `?${rest}` : '' }, { replace: true });
  }, [search, pathname, navigate]);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // 서랍(좁은 화면)의 바로가기는 헤더 드롭다운과 따로 접힌다 — 서랍에선 이것이 목록의 한 줄이라
  // 기본은 접힘이다. 펼친 상태를 헤더와 공유하면 서랍을 열 때마다 목록이 통째로 밀려난다.
  const [drawerShortcutsOpen, setDrawerShortcutsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);

  /*
    하위 메뉴 판(flyout) — 자식이 있는 맨 윗줄을 누르면 사이드바 오른쪽에 그 아래 나무가 선다
    (사용자 지시 2026-10-06). 그 줄 자신은 이동하지 않는다. 좁은 화면(lg 미만)에서는 판이 들어갈
    자리가 없어 같은 상태로 그 줄 아래에 펼친다.
  */
  const [flyoutKey, setFlyoutKey] = useState<string | null>(null);

  const sessionUser = session?.user ?? null;
  const close = () => {
    setMenuOpen(false);
    setFlyoutKey(null);
  };

  /*
    서랍(모바일)에 내려온 헤더 항목들의 공통 모양. <b>아래 메뉴 목록의 줄과 같은 치수</b>다
    (`renderItem` 의 px-4 py-2.5 text-sm) — 서랍 안에서 누르는 것이 한 가지 모양이어야
    「이건 버튼이고 저건 메뉴」 를 매번 다시 읽지 않는다.

    폭이 208px 라 긴 이름은 넘친다. `min-w-0` + `truncate` 로 줄이고, 줄바꿈은 하지 않는다 —
    두 줄이 되면 줄 높이가 제각각이 되어 다시 계단이 된다.
  */
  const drawerRowClass = 'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-slate-50 min-w-0';
  const drawerRowStyle = { color: 'var(--text-secondary)', textAlign: 'left' } as const;

  // 바로가기(홈페이지·블로그·그룹웨어…). 등록된 것이 없으면 버튼 자체를 그리지 않는다 —
  // 빈 서랍을 열게 두면 "고장 났나" 를 묻게 된다. 등록은 환경설정 > 바로가기 관리에서 한다.
  const shortcuts = useShortcutLinks();
  const shortcutLinks = shortcuts.data ?? [];
  const shortcutsRef = useRef<HTMLDivElement>(null);

  // 경로가 바뀌면 모바일 서랍을 닫고 검색어도 지운다. v1 은 링크마다 onClick 으로 닫았고,
  // 그래서 뒤로가기로 이동했을 때는 열린 채 남았다. 검색어는 이동하면 목적을 다한 것이라 함께 비운다.
  useEffect(() => {
    setMenuOpen(false);
    setMenuQuery('');
    setShortcutsOpen(false);
    setFlyoutKey(null);
  }, [pathname]);

  // 하위 메뉴 판은 바깥을 누르거나 Esc 로 닫는다. 판 자신과 여는 줄은 바깥이 아니다 —
  // 여는 줄을 다시 누르면 그 줄의 onClick 이 닫는다.
  useEffect(() => {
    if (!flyoutKey) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.('[data-flyout-panel],[data-flyout-trigger]')) return;
      setFlyoutKey(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFlyoutKey(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [flyoutKey]);

  // 사이드바를 접으면 판도 함께 닫는다 — 붙어 설 자리가 없어진다.
  useEffect(() => {
    if (collapsed) setFlyoutKey(null);
  }, [collapsed]);

  // 드롭다운은 바깥을 누르거나 Esc 로 닫는다. 헤더에 붙어 있어 화면을 가리므로,
  // 닫는 방법이 버튼 다시 누르기 하나뿐이면 다른 것을 누르려다 매번 걸린다.
  useEffect(() => {
    if (!shortcutsOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!shortcutsRef.current?.contains(event.target as Node)) setShortcutsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShortcutsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [shortcutsOpen]);

  // 메뉴 검색. 그룹은 펼치는 것이라 목적지가 아니므로, 실제로 갈 수 있는 항목만 평평하게 펴서 거른다.
  // 부모 이름으로도 걸리게 해 "설정" 을 치면 그 아래 화면들이 함께 나온다.
  //
  // 깊이를 고정하지 않고 훑는다 — 주간업무·월간업무 아래에 3단이 있고(DOC-24), 거기까지 내려가지 않으면
  // "준공현장" 을 검색해도 나오지 않는다. 트리에서 길을 잃은 사람의 마지막 안전망이 검색이다.
  const query = normalizeSearch(menuQuery);

  /*
    전자결재 서식 (사용자 지시 2026-10-22). 서식은 서버가 정하므로 표에 손으로 적지 않고 받아 온다.
    검색어를 친 뒤에만 받고, 전자결재가 닫힌 사람은 받지 않는다 — 기안이 열린 서식만 줄로 선다.
  */
  const approvalForms = useApprovalForms({ enabled: hasApprovalMenu && query !== '' });

  const searchResults = useMemo((): SearchHit[] | null => {
    if (!query) return null;

    const flat: Array<{ item: MenuConfigItem; groupLabel?: string }> = [];
    const walk = (items: MenuConfigItem[], trail?: string) => {
      for (const item of items) {
        if (item.itemType !== 'group') flat.push({ item, groupLabel: trail });
        walk(childrenOf(item.menuKey), trail ? `${trail} › ${item.label}` : item.label);
      }
    };
    walk(topLevel);

    const menuHits = flat.map((entry): SearchHit => ({
      kind: 'menu',
      ...entry,
      score: scoreMenuHit(entry.item, entry.groupLabel, query, closedMenuKeys),
    }));

    // 화면 안쪽 기능(마이페이지 탭·하위 탭). 메뉴 줄 뒤에 붙여 같은 점수면 메뉴가 앞에 선다.
    /*
      그 화면을 못 여는 사람에게는 줄도 없다 — 라우트가 막는 키와 같은 키를 본다. 트리의 메뉴 줄이 권한·게이트로
      걸러지는 것과 같은 규칙이라, 검색이 그 규칙을 우회하지 않는다.
    */
    const opens = (entry: (typeof FEATURE_SEARCH_ENTRIES)[number]) =>
      (!entry.adminOnly || isAdministrator(session?.user?.userType)) &&
      (!entry.menuKey ||
        (!closedMenuKeys.has(entry.menuKey) && resolveMenuCapability(session, entry.menuKey, 'view')));

    const featureHits = FEATURE_SEARCH_ENTRIES.filter(opens).map((entry): SearchHit => ({
      kind: 'feature',
      key: entry.key,
      label: entry.label,
      trail: entry.trail,
      href: entry.href,
      icon: iconNameOf(entry.iconKey),
      score: scoreFeatureHit(entry.label, entry.aliases, entry.trail, query),
    }));

    const formHits: SearchHit[] = hasApprovalMenu
      ? groupForms(approvalForms.data ?? [])
          .flatMap((group) => group.forms.map((form) => ({ form, category: group.category })))
          .filter(({ form }) => isFormEnabled(form))
          .map(({ form, category }): SearchHit => {
            const trail = `전자결재 › 기안 › ${category}`;
            return {
              kind: 'feature',
              key: `approval_form_${form.formKey}`,
              label: form.name,
              trail,
              href: `/approval/draft?formKey=${encodeURIComponent(form.formKey)}`,
              icon: iconNameOf('approval'),
              score: scoreFeatureHit(form.name, FORM_SEARCH_ALIASES[form.formKey] ?? [], trail, query),
            };
          })
      : [];

    return [...menuHits, ...featureHits, ...formHits]
      .filter((entry) => entry.score > 0)
      /*
        점수 내림차순. <b>같은 점수 안에서는 트리 순서</b>가 유지된다 — `Array.sort` 가 안정
        정렬이라 원래 순서가 그대로 남고, 그래야 「검색했더니 줄이 매번 다른 데 있다」 가 안 된다.
      */
      .sort((a, b) => b.score - a.score);
  }, [query, topLevel, childrenOf, closedMenuKeys, hasApprovalMenu, approvalForms.data, session]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsMobile(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener('change', sync);
    return () => mediaQuery.removeEventListener('change', sync);
  }, []);

  const handleLogout = () => {
    logout.mutate(undefined, {
      // 세션이 사라진 뒤 남아 있던 화면이 401 을 뿜지 않도록 곧바로 로그인 화면으로 보낸다.
      onSettled: () => window.location.assign('/login'),
    });
  };

  /**
   * 즐겨찾기로 세울 줄들 — <b>지금 트리에 있는 것만</b> (V325).
   *
   * <p>키로 찾아 쓰므로 메뉴가 없어지거나(전자결재처럼) 권한이 내려가면 그 줄은 조용히 빠진다.
   * 담아 둔 값을 지우지는 않는다 — 권한이 돌아오면 그 별도 돌아온다.
   *
   * <h2>그룹도 담는다 (2026-09-22 · 「인사관리 별표 하면 상단에 안뜨는데?」)</h2>
   * 처음에는 여기서 그룹을 걸러 냈다({@code itemType !== 'group'}). 그런데 <b>별은 모든 줄에
   * 붙어 있다</b>({@code renderItem}) — 인사관리·환경설정처럼 주소가 없는 줄을 누르면 별이
   * 켜지고 서버에도 담기는데, 이 지도에 없어서 <b>즐겨찾기 칸에는 끝내 안 뜬다.</b>
   * 켜지는 별과 서는 줄이 어긋나면 사람은 기능이 고장 났다고 읽는다.
   *
   * <p>그룹이 담길 수 있는 이유는 <b>갈 곳이 있기 때문</b>이다 — 납작해진 사이드바에서 그런 줄은
   * 첫 자식으로 데려간다({@link landingHrefOf}). 즐겨찾기 줄도 같은 곳으로 보낸다.
   *
   * <h2>거쳐 온 길을 함께 들고 온다 (2026-09-30)</h2>
   * 화면 위 탭에도 별이 붙은 뒤로({@code MenuTabs}) 여기 서는 이름은 두세 단 아래의 것일 수 있다.
   * 「연차 촉진」 만 적으면 어느 묶음의 것인지 알 수 없어, 트리를 훑는 길에 <b>부모 이름들</b>을
   * 그대로 들고 온다 — 라벨은 관리자가 고치는 값이라 담아 두지 않고 매번 지금 트리에서 집는다.
   */
  const favoriteItems = useMemo(() => {
    const byKey = new Map<string, { item: MenuConfigItem; trail: string[] }>();
    const walk = (items: MenuConfigItem[], trail: string[]) => {
      for (const item of items) {
        byKey.set(item.menuKey, { item, trail });
        walk(childrenOf(item.menuKey), [...trail, item.label]);
      }
    };
    walk(topLevel, []);
    return favoriteKeys
      .map((key) => byKey.get(key))
      .filter((found): found is { item: MenuConfigItem; trail: string[] } => !!found)
      // 갈 곳이 없는 줄은 빼고 담는다 — 자식이 전부 권한 밖이면 그룹만 남아 눌러도 아무 일이 없다.
      .map(({ item, trail }) => ({ item, trail, href: landingHrefOf(item, childrenOf) }))
      .filter(
        (row): row is { item: MenuConfigItem; trail: string[]; href: string } => row.href !== null,
      );
  }, [favoriteKeys, topLevel, childrenOf]);

  /** 별을 한 칸 위·아래로. 서버에는 <b>목록 전체</b>를 보낸다(근거는 {@code MenuFavoriteService}). */
  const moveFavorite = (menuKey: string, step: -1 | 1) => {
    const order = favoriteItems.map((row) => row.item.menuKey);
    const at = order.indexOf(menuKey);
    const to = at + step;
    const moving = order[at];
    const shifted = order[to];
    // 자리를 이미 확인했지만 타입은 그 확인을 모른다(`noUncheckedIndexedAccess`).
    if (at < 0 || to < 0 || to >= order.length || moving === undefined || shifted === undefined) return;
    order[at] = shifted;
    order[to] = moving;
    // 담아 뒀지만 지금 안 보이는 키(권한이 내려간 메뉴)는 서버가 뒤에 그대로 남긴다.
    favoriteActions.reorder.mutate(order);
  };

  /**
   * 사이드바 한 줄 — <b>맨 윗줄만</b> 선다 (사용자 지시 2026-09-22).
   *
   * <h2>나무를 걷었다</h2>
   * 예전에는 이 함수가 자기 자식을 다시 그리며 트리를 만들었다. 그 모양에는 값이 둘 있었다 —
   * 줄마다 화살표 칸이 자리를 먹었고, 하위 화면을 보려면 <b>지금 보는 화면과 상관없이</b> 왼쪽을
   * 한 번 더 눌러야 했다. 이제 하위는 화면 맨 위의 탭으로 간다({@link MenuTabs}).
   *
   * <p>비워진 화살표 자리에 <b>별</b>이 들어간다 — 「Tree 구조로 보여주던 화살표 칸에 별표를」.
   *
   * <h2>그룹도 눌리면 간다</h2>
   * 설정·인사관리처럼 주소가 없는 줄은 예전에 «펼치기»가 전부였다. 납작해진 뒤로 그런 줄은
   * 눌러도 아무 일이 없는 줄이 되므로, <b>첫 자식으로</b> 데려간다({@code landingHrefOf}).
   */
  /**
   * 하위 메뉴 판의 한 마디. 그룹은 굵은 제목으로 서고, 그 자식은 왼쪽 세로선에 ├ └ 로 매달린다
   * (사용자 지시 2026-10-06 「Tree 를 더 쉽게, 칸마다 회색 선」). 눌리는 줄은 전부 회색 테두리
   * 칸이고 오른쪽에 별이 선다 — 판이 생기며 하위 메뉴를 담을 별 자리가 사라졌던 것을 되돌린다.
   * 링크가 자식을 가지면(주간업무) 그 칸도 눌리고 자식이 그 아래에 매달린다.
   * 판과 좁은 화면의 펼침이 같은 것을 그린다.
   */
  const renderFlyoutNode = (item: MenuConfigItem): ReactNode => {
    const children = childrenOf(item.menuKey);
    const branch = children.length > 0 ? renderFlyoutBranch(children) : null;

    if (item.itemType === 'group') {
      return (
        <div key={item.menuKey} className="space-y-1 pt-1">
          <p className="truncate" style={{ padding: '2px 4px', fontSize: '12px', color: 'var(--text-muted)' }}>
            {item.label}
          </p>
          {branch}
        </div>
      );
    }

    const active = children.length > 0 ? item.href != null && pathname === item.href : isActive(item, pathname);
    const linkClass = 'flex-1 min-w-0 flex items-center gap-2 px-2.5 py-1.5 text-sm';
    // 하위가 있어도 글씨는 다른 칸과 같다(사용자 지시 2026-10-06 「굵은 글씨 빼줘」) — 묶임은 연결선이 말한다.
    const linkStyle = {
      color: active ? 'var(--accent)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400,
    } as const;
    const body = (
      <>
        <span className="truncate">{item.label}</span>
        {hasBadge(item.menuKey) && <NewBadge />}
      </>
    );

    let link: ReactNode = null;
    if (item.itemType === 'external' && item.href) {
      link = (
        <a href={item.href} onClick={close} target="_blank" rel="noreferrer" className={linkClass} style={linkStyle}>
          {body}
        </a>
      );
    } else {
      const href = landingHrefOf(item, childrenOf);
      if (href) {
        link = (
          <Link to={href} onClick={close} className={linkClass} style={linkStyle}>
            {body}
          </Link>
        );
      }
    }

    return (
      <div key={item.menuKey} className="space-y-1">
        {link && (
          <div
            className={`group flex items-center rounded-md transition-colors ${active ? '' : 'bg-white hover:bg-slate-50'}`}
            style={{
              border: `1px solid ${active ? '#bfdbfe' : 'var(--border)'}`,
              ...(active ? { background: '#eff6ff' } : {}),
            }}
          >
            {link}
            <MenuFavoriteStar
              on={favoriteSet.has(item.menuKey)}
              label={item.label}
              className={favoriteSet.has(item.menuKey) ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}
              onToggle={() => favoriteActions.toggle.mutate({
                menuKey: item.menuKey,
                favorite: !favoriteSet.has(item.menuKey),
              })}
            />
          </div>
        )}
        {branch}
      </div>
    );
  };

  /**
   * 한 부모 아래 자식들 — 왼쪽 세로선에 가로선으로 매단다. 마지막 자식은 세로선이 그 칸 가운데서
   * 끝난다(└). 칸 높이가 줄마다 같아 가운데를 상수({@code FLYOUT_ROW_MID})로 잡는다.
   */
  const renderFlyoutBranch = (items: MenuConfigItem[]): ReactNode => (
    <div className="space-y-1" style={{ marginLeft: '8px' }}>
      {items.map((child, index) => {
        const last = index === items.length - 1;
        return (
          <div key={child.menuKey} className="relative" style={{ paddingLeft: '14px' }}>
            <span
              aria-hidden
              className="absolute"
              style={{
                left: 0,
                top: '-4px',
                ...(last ? { height: `${FLYOUT_ROW_MID + 4}px` } : { bottom: 0 }),
                borderLeft: `1px solid ${FLYOUT_LINE}`,
              }}
            />
            <span
              aria-hidden
              className="absolute"
              style={{ left: 0, top: `${FLYOUT_ROW_MID}px`, width: '10px', borderTop: `1px solid ${FLYOUT_LINE}` }}
            />
            {renderFlyoutNode(child)}
          </div>
        );
      })}
    </div>
  );

  const flyoutItem = flyoutKey ? topLevel.find((item) => item.menuKey === flyoutKey) ?? null : null;

  const renderItem = (item: MenuConfigItem) => {
    const active = isActive(item, pathname);
    /*
      <b>평소에는 안 보인다</b> — 줄마다 별이 켜져 있으면 사이드바가 표처럼 읽히고, 정작 눌러야 할
      이름이 뒤로 물러난다. 마우스를 올린 줄과 <b>이미 담은 줄</b>에만 선다(담은 별을 남겨 두는 것은
      되돌릴 자리를 그 줄 자신에 두기 위해서다).
    */
    const star = (
      <MenuFavoriteStar
        on={favoriteSet.has(item.menuKey)}
        label={item.label}
        className={`hover:bg-slate-50 ${favoriteSet.has(item.menuKey) ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
        onToggle={() => favoriteActions.toggle.mutate({
          menuKey: item.menuKey,
          favorite: !favoriteSet.has(item.menuKey),
        })}
      />
    );

    const body = (
      <>
        <MenuIcon name={iconNameOf(item.menuKey)} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
        {hasBadge(item.menuKey) && <NewBadge />}
        {item.menuKey === 'approval' && approvalCount > 0 && <CountBadge n={approvalCount} />}
      </>
    );

    const rowStyle = {
      flex: 1,
      minWidth: 0,
      paddingLeft: '12px',
      background: active ? '#eff6ff' : 'transparent',
      color: active ? 'var(--accent)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400,
    } as const;

    if (item.itemType === 'external' && item.href) {
      return (
        <div key={item.menuKey} className="group flex items-center" data-tour={`sidebar-${item.menuKey}`}>
          <a
            href={item.href}
            onClick={close}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2.5 py-2 rounded-lg text-sm transition-all"
            style={rowStyle}
          >
            {body}
          </a>
          {star}
        </div>
      );
    }

    /*
      자식이 있으면 이동하지 않고 하위 메뉴 판을 연다(사용자 지시 2026-10-06). 같은 줄을 다시 누르면
      닫는다. 좁은 화면에서는 판 대신 이 줄 아래에 펼친다(`lg:hidden`).
    */
    if (childrenOf(item.menuKey).length > 0) {
      const open = flyoutKey === item.menuKey;
      return (
        <Fragment key={item.menuKey}>
          <div className="group flex items-center" data-tour={`sidebar-${item.menuKey}`}>
            <button
              type="button"
              data-flyout-trigger={item.menuKey}
              onClick={() => setFlyoutKey(open ? null : item.menuKey)}
              aria-expanded={open}
              className="flex items-center gap-2.5 py-2 rounded-lg text-sm transition-all"
              style={{
                ...rowStyle,
                background: active ? '#eff6ff' : open ? '#f1f5f9' : 'transparent',
                textAlign: 'left',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {body}
            </button>
            {star}
          </div>
          {open && (
            <div className="lg:hidden space-y-1 pb-1 pl-3 pr-1" data-flyout-panel>
              {childrenOf(item.menuKey).map((child) => renderFlyoutNode(child))}
            </div>
          )}
        </Fragment>
      );
    }

    // 그룹에는 주소가 없다 — 첫 자식으로 데려간다(위 머리말). 그것마저 없으면 그릴 것이 없다.
    const href = landingHrefOf(item, childrenOf);
    if (!href) return null;

    return (
      <div key={item.menuKey} className="group flex items-center" data-tour={`sidebar-${item.menuKey}`}>
        <Link
          to={href}
          onClick={close}
          className="flex items-center gap-2.5 py-2 rounded-lg text-sm transition-all"
          style={rowStyle}
        >
          {body}
        </Link>
        {star}
      </div>
    );
  };

  // 검색 결과 한 줄. 트리를 접었다 폈다 하지 않고 목적지로 바로 가도록, 부모 그룹 이름을 함께 보여준다.
  const renderSearchResult = (item: MenuConfigItem, groupLabel?: string) => {
    const active = isActive(item, pathname);
    const body = (
      <>
        <MenuIcon name={iconNameOf(item.menuKey)} />
        <span className="min-w-0 flex-1">
          <span className="block truncate">{item.label}</span>
          {groupLabel && (
            <span className="block truncate" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {groupLabel}
            </span>
          )}
        </span>
        {hasBadge(item.menuKey) && <NewBadge />}
      </>
    );
    const className = 'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all hover:bg-slate-50';

    if (item.itemType === 'external' && item.href) {
      return (
        <a
          key={item.menuKey}
          href={item.href}
          onClick={close}
          target="_blank"
          rel="noreferrer"
          className={className}
          style={{ color: 'var(--text-secondary)' }}
        >
          {body}
        </a>
      );
    }

    return (
      <Link
        key={item.menuKey}
        to={item.href ?? '#'}
        onClick={close}
        className={className}
        style={{
          background: active ? '#eff6ff' : 'transparent',
          color: active ? 'var(--accent)' : 'var(--text-secondary)',
          fontWeight: active ? 600 : 400,
        }}
      >
        {body}
      </Link>
    );
  };

  // 기능 줄(마이페이지 탭 · 결재 서식). 메뉴 줄과 같은 모양이고, 아래 회색 글씨가 가는 곳이다.
  const renderFeatureResult = (hit: Extract<SearchHit, { kind: 'feature' }>) => (
    <Link
      key={hit.key}
      to={hit.href}
      onClick={close}
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all hover:bg-slate-50"
      style={{ color: 'var(--text-secondary)' }}
    >
      <MenuIcon name={hit.icon} />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{hit.label}</span>
        <span className="block truncate" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          {hit.trail}
        </span>
      </span>
    </Link>
  );

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-40 h-14 flex items-center px-3 sm:px-4 gap-2 sm:gap-3 no-print bg-white"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        {showSidebar && (
          <button
            className="hidden lg:inline-flex p-2 rounded-lg hover:bg-slate-100 flex-shrink-0"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
            aria-expanded={!collapsed}
            title={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-secondary)' }}>
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        <Link
          to="/home"
          className="flex items-center rounded-lg hover:bg-slate-50 min-w-0 flex-shrink"
          style={{ padding: isMobile ? '4px 4px' : '4px 6px' }}
        >
          {/* `BoMI`·`Lounge` 둘 다 CI 그림이다(DOC-51 §6) — 좁은 화면에서는
              뒤의 `그룹웨어` 만 빼고 상표는 그대로 둔다.
              헤더 막대는 h-14 고정이라 이 숫자를 올려도 막대는 높아지지 않는다. */}
          <BrandMark height={isMobile ? 18 : 22} wordmark={!isMobile} />
        </Link>

        {/*
          프로그램 다운로드 — 상표와 오른쪽 버튼 묶음 <b>사이의 가운데 칸</b>, 기간 안에만 선다
          (사용자 지시 2026-10-02: 「관리자 대시보드의 왼쪽, 헤더의 정중앙쪽」). 오른쪽 묶음에 끼우면
          여덟 번째 버튼이 되어 눈에 안 띈다 — 빈 가운데에 혼자 서야 «지금 눌러야 하는 것» 으로 읽힌다.
          자료실과 같은 빛나는 옷(`.btn-shine`)인 이유도 그것이다. 기간이 지나면 저절로 사라진다.

          <p>좁은 화면(lg 미만)에서는 이 칸을 빼고 서랍의 같은 줄이 길을 맡는다 — 오른쪽 묶음과 같은 문턱.
        */}
        {/*
          AI Agent 모드(사용자 지시 2026-10-26) — 같은 가운데 칸이다. <b>개발 빌드에만</b> 선다:
          화면이 시안이라 라우트도 `import.meta.env.DEV` 에서만 있다(`router.tsx`).
        */}
        {sessionUser && (activeProgram || import.meta.env.DEV) && (
          <div className="hidden lg:flex flex-1 justify-center items-center gap-2 min-w-0">
            {activeProgram && (
              <button
                onClick={receiveProgram}
                disabled={programReceiving}
                data-tour="header-program-download"
                className="btn-secondary btn-shine"
                style={{ fontSize: '12px', padding: '6px 14px', whiteSpace: 'nowrap' }}
              >
                {programReceiving ? '받는 중…' : `${activeProgram.name} 다운로드`}
              </button>
            )}
            {import.meta.env.DEV && (
              <button
                onClick={() => navigate('/aiagent/mode')}
                className="btn-secondary btn-shine"
                style={{
                  fontSize: '12px',
                  padding: '6px 14px',
                  whiteSpace: 'nowrap',
                  ...(pathname === '/aiagent/mode' ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : {}),
                }}
              >
                AI Agent 모드
              </button>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-1 sm:gap-2 flex-nowrap min-w-0">
          {sessionUser && (
            <>
              {/*
                자료실 도크 — <b>AI Agent 왼쪽</b>이다(사용자 지시 2026-09-04). 열어 두면 화면을
                옮겨도 남고, 그 안의 파일을 끌어다 대화창·서류칸에 참조로 놓는다.

                <p><b>도크를 열 수 있는 자리에서만 뜬다</b>(사용자 지시 2026-09-04). 못 여는 자리
                (홈·자료실 화면 자신·모바일·좁은 화면)에서는 자료실 화면으로 가는 링크로 남겨 두었다가
                걷어냈다 — 이 버튼의 뜻은 <b>「옆에 펴기」</b> 하나여야 한다. 같은 이름이 어떤 탭에서는
                판을 펴고 어떤 탭에서는 화면을 옮기면, 누르기 전에는 무슨 일이 날지 알 수 없다.
                화면으로 가는 길은 왼쪽 메뉴의 자료실이 이미 맡고 있어 잃는 것도 없다.

                <p>판정은 {@link AppLayout} 의 `storageDockAvailable` 한 곳이 한다 — 여기서 폭·경로를
                다시 따지면 버튼이 있는데 안 열리는(또는 그 반대의) 날이 온다.
              */}
              {storageDockAvailable && (
                <button
                  onClick={onToggleStorageDock}
                  // 현장 정보의 「전체 대시보드」와 <b>같은 옷</b>이다(`.btn-shine` · 사용자 지시
                  // 2026-09-04). 둘 다 «여기로 들어가는 입구» 라, 빛이 무엇을 뜻하는지 한 번만
                  // 배우면 되게 한다. 열려 있으면 그 빛은 저절로 꺼진다(index.css §aria-pressed).
                  className="btn-secondary btn-shine"
                  aria-pressed={storageDockOpen}
                  style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '6px 8px' : '6px 12px', whiteSpace: 'nowrap' }}
                >
                  자료실
                </button>
              )}
              {/*
                AI Agent 버튼은 헤더에서 내렸다 — 오른쪽 아래의 떠 있는 동그라미가 그 문이다
                ({@link AiAgentBubble}, 사용자 지시 2026-09-17). 같은 문을 둘로 두지 않는다.
              */}
              {/*
                메일함(봉투) 버튼은 2026-09-18 에 헤더에서 내렸다가 2026-10-02 에 <b>[메일함]</b> 으로
                돌아왔다 — 이번에는 웹메일 링크가 아니라 하이웍스 SSO 로 로그인 없이 들어간다
                (`HiworksMailButton` · DOC-108). 자리는 관리자 대시보드의 <b>왼쪽</b>(사용자 지시).
                옛 주소(`menuConfig.MAILBOX_EXTERNAL_URL`)는 환경설정 › 외부 링크에 그대로 있다.
              */}
              {/*
                좁은 화면에서는 이 묶음을 헤더에서 뺀다({@code hidden lg:contents}).
                버튼이 전부 `whiteSpace: nowrap` 이라 줄지 않는데, 여덟 개를 한 줄에 세우면 폭이
                600px 을 넘어 <b>맨 끝의 햄버거(☰)가 화면 밖으로 밀려난다</b> — 메뉴를 열 방법이
                사라진다. 대신 아래 서랍 위쪽에 같은 항목을 모아 둔다.

                <p><b>문턱은 md(768)가 아니라 lg(1024)다</b>(2026-09-16). ☰ 는 사이드바가 서랍으로
                물러나는 lg 미만 <b>전 구간</b>에서 메뉴로 가는 유일한 길인데, 묶음이 md 부터 펴지면
                768~1023 에서 둘이 겹쳐 서다 ☰ 가 밀려난다 — 사이드바도 없고 ☰ 도 없는, 메뉴가
                통째로 사라진 화면이 된다. 짝인 서랍 쪽 `lg:hidden` 과 <b>같은 값</b>이어야 한다.

                `lg:contents` 인 이유: 데스크톱에서는 이 div 가 없는 것처럼 되어야 자식들이
                헤더 flex 줄에 그대로 서고, 간격(gap)도 예전과 같이 매겨진다.
              */}
              <div className="hidden lg:contents">
              {/*
                관리자 대시보드 — 관리자(admin·super_admin)에게 보이는 운영 화면. 메뉴가 아니라 헤더에 둔다.
                한때 최고 관리자 전용이었으나 admin 까지 열었다(사용자 지시 2026-09-01) — 서버가 느리다는
                말을 듣고 움직이는 사람이 곧 관리자다. 안쪽의 DB 운영 콘솔 링크만 최고 관리자에게 남는다.
                게이트를 함께 보지만, 관리자는 게이트 밖이라(`closedKeysFor`) 실제로는 언제나 보인다 —
                오픈 관리 모달의 스위치가 거짓말을 하지 않도록 판정 자리만 맞춰 둔다.
              */}
              <HiworksMailButton
                tour="header-mail"
                className="btn-secondary"
                style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '6px 8px' : '6px 12px', whiteSpace: 'nowrap' }}
              />
              {isAdministrator(sessionUser.userType) && !closedMenuKeys.has('admin_dashboard') && (
                <Link
                  to="/admin/dashboard"
                  className="btn-secondary"
                  style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '6px 8px' : '6px 12px', whiteSpace: 'nowrap' }}
                >
                  {isMobile ? '관리자' : '관리자 대시보드'}
                </Link>
              )}
              {/*
                바로가기 — 회사 홈페이지·블로그·그룹웨어처럼 <b>이 시스템 바깥</b>으로 나가는 주소들.
                사이드바 메뉴가 아니라 헤더에 둔다: 메뉴는 권한·배지·하위 트리를 갖는 화면의 골격인데
                이것들은 그중 무엇도 갖지 않는 즐겨찾기이고, 전 직원이 어느 화면에서나 같은 곳으로 나간다.
                목록(이름·주소)은 환경설정 > 바로가기 관리에서 관리한다(`settings_shortcuts`).
              */}
              {shortcutLinks.length > 0 && (
                <div ref={shortcutsRef} className="relative flex-shrink-0">
                  <button
                    onClick={() => setShortcutsOpen((open) => !open)}
                    className="btn-secondary inline-flex items-center gap-1"
                    aria-haspopup="menu"
                    aria-expanded={shortcutsOpen}
                    style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '6px 8px' : '6px 12px', whiteSpace: 'nowrap' }}
                  >
                    바로가기
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      style={{ transform: shortcutsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  {shortcutsOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-1 w-56 rounded-lg bg-white shadow-lg z-50 py-1"
                      style={{ border: '1px solid var(--border)' }}
                    >
                      {shortcutLinks.map((link) => (
                        <a
                          key={`${link.label}-${link.url}`}
                          href={link.url}
                          // 새 탭으로 연다 — 여기서 나가는 곳은 전부 다른 시스템이라,
                          // 같은 탭에서 열면 보던 화면(작성 중이던 글)을 잃는다.
                          target="_blank"
                          rel="noreferrer noopener"
                          role="menuitem"
                          onClick={() => setShortcutsOpen(false)}
                          className="block px-3 py-2 hover:bg-slate-50"
                        >
                          <span className="block truncate" style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                            {link.label}
                          </span>
                          <span className="block truncate" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {link.description || link.url}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Link
                to="/mypage"
                data-tour="header-mypage"
                className="btn-secondary"
                style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '6px 8px' : '6px 12px', whiteSpace: 'nowrap' }}
              >
                {isMobile ? '마이' : `${sessionUser.name} · 마이페이지`}
              </Link>
              {/*
                전자결재 — <b>이 버튼이 유일한 입구</b>다 (사용자 지시 2026-09-22: 「좌측 전자결재
                메뉴를 없애고 이거로 통일할거라」). 사이드바 줄을 걷었고, 그 화면 하나에 결재함과
                기안 서식이 함께 선다({@code MyApprovalPage}).

                <p>이 묶음(`hidden lg:contents`)은 lg 미만에서 헤더에서 빠지므로, <b>서랍에도 같은
                줄을 세웠다</b>(아래 `lg:hidden` 묶음). 예전에는 좁은 화면에서 사이드바의 「전자결재」
                가 그 길이었는데, 그 줄이 없어졌으니 여기서 길을 내주지 않으면 폰·패드에서 결재함에
                닿을 방법이 사라진다.

                <p>조건은 <b>권한과 오픈 게이트</b>다(`hasApprovalMenu` 머리말). 숫자는 두 자리가
                같은 값을 쓴다 — 헤더와 서랍이 어긋나면 어느 쪽이 맞는지 아무도 모른다.
              */}
              {hasApprovalMenu && (
                <Link
                  to="/approval/draft"
                  data-tour="header-approval"
                  className="btn-secondary inline-flex items-center gap-1.5"
                  style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '6px 8px' : '6px 12px', whiteSpace: 'nowrap' }}
                >
                  전자결재
                  {approvalCount > 0 && <CountBadge n={approvalCount} />}
                </Link>
              )}
              {/*
                직원찾기 — 조직도에서 사람을 찾아 연락처·오늘 상태를 본다. 메뉴가 아니라 헤더에 둔다:
                메뉴 권한과 무관하게 전 직원이 쓰는 도구이고(사내 주소록), 어느 화면을 보다가도
                "이 사람 지금 자리에 있나" 를 묻게 되므로 화면을 떠나지 않고 열려야 한다.
              */}
              <button
                onClick={() => setShowDirectoryModal(true)}
                data-tour="header-directory"
                className="btn-secondary"
                style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '6px 8px' : '6px 12px', whiteSpace: 'nowrap' }}
              >
                {isMobile ? '직원' : '직원찾기'}
              </button>
              {/*
                사용 안내. 누르면 <b>지금 보고 있는 화면</b>의 설명이 열린다({@code lib/tour/pageGuides.ts}).
                홈에서 누르면 처음 로그인 때와 같은 전체 안내가 나온다 — 홈은 특정 업무 화면이 아니라 입구라서다.
                글자 없이 물음표만 두는 것은 헤더가 이미 빽빽해서다 — 모바일에서는 버튼 하나 폭이 아쉽다.
              */}
              <button
                onClick={onStartTour}
                data-tour="header-help"
                className="btn-secondary"
                aria-label="이 화면 사용 안내 보기"
                title="이 화면 사용 안내 보기"
                style={{
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: 700,
                  padding: isMobile ? '6px 9px' : '6px 10px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                ?
              </button>
              {/*
                로그아웃은 헤더에 없다 — 마이페이지 안으로 옮겼다(사용자 지시 2026-09-17).
                헤더는 이동하는 곳인데, 되돌릴 수 없는 동작이 「직원찾기」·「?」 옆에 붙어 있어
                누르려던 것과 한 뼘 차이였다. 지금은 위 「마이페이지」 → 개인정보 설정 맨 아래다.
              */}
              </div>
            </>
          )}
          {/*
            개선사항 요청 — <b>창이 아니라 화면으로 간다</b> (사용자 지시 2026-09-22).

            <p>예전에는 이 버튼이 접수 창({@code ImprovementRequestModal})을 열었고, 사이드바에는
            같은 곳을 읽는 「개선요청」 줄이 따로 있었다. 문이 둘이라 <b>낸 사람이 자기 글을 어디서
            보는지</b>가 매번 달랐다 — 창으로 낸 사람은 접수 뒤 아무것도 못 봤다.

            <p>지금은 하나다. 눌러서 <b>목록으로 가고</b>, 그 화면의 「등록」 이 접수다. 사이드바 줄은
            내렸다({@code menuConfig} 의 `RETIRED_MENU_KEYS`) — 헤더에 늘 떠 있는 빨간 버튼이
            사이드바 한 줄보다 눈에 먼저 들어온다.

            ★ 색과 자리는 그대로다. 이 버튼의 빨강은 「고장 났을 때 누르는 곳」 을 뜻하게 굳었고,
              화면 안내(`pageGuides`)도 그 색으로 사람을 보낸다.
          */}
          <Link
            to="/requests"
            data-tour="header-improvement"
            className="btn-primary"
            style={{
              fontSize: '12px',
              padding: isMobile ? '6px 10px' : '6px 12px',
              whiteSpace: 'nowrap',
              background: '#dc2626',
              color: '#ffffff',
              textDecoration: 'none',
            }}
          >
            개선사항 요청
          </Link>
          {showSidebar && (
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 flex-shrink-0"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="메뉴 열기"
              aria-expanded={menuOpen}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-secondary)' }}>
                {menuOpen ? <path d="M6 18L18 6M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          )}
        </div>
      </header>

      {showSidebar && (
      <aside
        className={`fixed left-0 top-14 bottom-0 w-52 z-[60] flex flex-col overflow-hidden no-print bg-white transition-transform duration-200 ${menuOpen ? 'translate-x-0' : '-translate-x-full'} ${collapsed ? 'lg:-translate-x-full' : 'lg:translate-x-0'}`}
        style={{ borderRight: '1px solid var(--border)' }}
      >
        {/*
          좁은 화면 전용 — 헤더에서 뺀 항목들이 여기로 내려온다(위 `hidden lg:contents` 참고).
          서랍이 모바일에서 이것들에 닿는 유일한 길이므로 메뉴 목록보다 위, 스크롤되지 않는 자리에 둔다.

          <h3>알약 더미를 걷고 목록으로 바꿨다 (사용자 지적 2026-08-31)</h3>
          예전에는 여섯 개가 `flex-wrap` 으로 흘렀다. 서랍 폭이 208px 이라 어느 것도 한 줄에 둘씩
          들어가지 못해 <b>줄마다 남는 폭이 제각각인 계단</b>이 되고, 「관리자 대시보드」 같은 긴
          이름은 혼자 한 줄을 먹었다. 바로가기가 두어 개 등록된 회사에서는 그 더미가 서랍 높이의
          절반을 차지해, 정작 <b>메뉴 목록이 화면 밖으로 밀렸다.</b>

          <p>지금은 아래 메뉴 목록과 <b>같은 모양의 세로 줄</b>이다 — 서랍 안에서 누르는 것이
          한 가지 모양이 되고, 항목이 몇 개든 높이가 예측된다.

          <p><b>로그아웃은 여기 없다.</b> 서랍 맨 아래로 내렸다 — 목록 맨 위에서 「직원찾기」 옆에
          붙어 있으면 메뉴를 고르려다 눌린다. 되돌릴 수 없는 동작은 이동과 섞지 않는다.
        */}
        {sessionUser && (
          <div className="lg:hidden flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
            {/*
              누구로 들어와 있는지 — 서랍을 연 사람이 가장 먼저 보는 줄이다. 이름을 알약 안에
              구겨 넣지 않고 한 줄을 온전히 준다(긴 이름도 잘리지 않는다).
            */}
            <Link
              to="/mypage"
              onClick={close}
              className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-slate-50"
              style={{ color: 'var(--text-primary)' }}
            >
              <MenuIcon name="user" />
              <span className="min-w-0">
                <span className="block truncate" style={{ fontSize: '13px', fontWeight: 700 }}>
                  {sessionUser.name}
                </span>
                <span className="block" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  마이페이지
                </span>
              </span>
            </Link>

            <button onClick={() => { close(); setShowDirectoryModal(true); }} className={drawerRowClass} style={drawerRowStyle}>
              <MenuIcon name="users" />
              직원찾기
            </button>

            {/*
              전자결재 — 사이드바 줄이 없어진 뒤로(2026-09-22) 좁은 화면에서 결재함에 닿는
              <b>유일한 길</b>이다. 건수는 헤더 버튼과 같은 값이다.
            */}
            {hasApprovalMenu && (
              <Link to="/approval/draft" onClick={close} className={drawerRowClass} style={drawerRowStyle}>
                <MenuIcon name="check" />
                <span className="truncate">전자결재</span>
                {approvalCount > 0 && <CountBadge n={approvalCount} />}
              </Link>
            )}

            {onStartTour && (
              <button onClick={() => { close(); onStartTour(); }} className={drawerRowClass} style={drawerRowStyle}>
                <MenuIcon name="message" />
                사용 안내
              </button>
            )}

            {/*
              메일함 — 헤더와 같은 문. 좁은 화면에서는 이 줄이 유일한 길이다.
              이름도 헤더와 같다: 서랍이 닫혀 있으면 이 줄은 보이지 않아 목록에 실리지 않고
              (`collectScreenAnchors` 의 `isVisible`), 열려 있으면 둘 중 보이는 쪽만 실린다.
            */}
            <HiworksMailButton tour="header-mail" onActivate={close} className={drawerRowClass} style={drawerRowStyle}>
              <MenuIcon name="message" />
              메일함
            </HiworksMailButton>

            {isAdministrator(sessionUser.userType) && !closedMenuKeys.has('admin_dashboard') && (
              <Link to="/admin/dashboard" onClick={close} className={drawerRowClass} style={drawerRowStyle}>
                <MenuIcon name="chart" />
                관리자 대시보드
              </Link>
            )}

            {/* 프로그램 다운로드 — 헤더와 같은 조건(기간 안)으로 선다. 좁은 화면에서는 이 줄이 유일한 길이다. */}
            {activeProgram && (
              <button
                onClick={() => { close(); receiveProgram(); }}
                disabled={programReceiving}
                className={drawerRowClass}
                style={{ ...drawerRowStyle, color: 'var(--accent)', fontWeight: 600 }}
              >
                <MenuIcon name="file" />
                <span className="truncate">{activeProgram.name} 다운로드</span>
              </button>
            )}

            {/*
              바깥으로 나가는 주소들. <b>한 줄로 접어 둔다</b> — 바로가기가 여섯 개 등록된 회사에서는
              여섯 줄이 서랍 위쪽을 다 먹어 정작 <b>메뉴 목록이 화면 밖으로 잘렸다</b>(사용자 지적 2026-09-22).
              아래 메뉴 그룹과 같은 접기 모양이라 서랍 안에서 누르는 법이 하나로 유지된다.
              ↗ 로 「여기를 떠난다」 를 알린다(새 탭으로 열린다).
            */}
            {shortcutLinks.length > 0 && (
              <>
                <button
                  onClick={() => setDrawerShortcutsOpen((open) => !open)}
                  className={drawerRowClass}
                  style={drawerRowStyle}
                  aria-expanded={drawerShortcutsOpen}
                >
                  <MenuIcon name="grid" />
                  <span className="truncate">바로가기</span>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ marginLeft: 'auto', flexShrink: 0, transform: drawerShortcutsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {drawerShortcutsOpen && (
                  // 펼쳤을 때만 높이를 쓰고, 그마저도 서랍 절반을 넘기지 않는다 —
                  // 등록이 많아도 아래 메뉴 목록이 화면 밖으로 밀리지 않게 여기서 스크롤한다.
                  <div className="overflow-y-auto" style={{ maxHeight: '40vh' }}>
                    {shortcutLinks.map((link) => (
                      <a
                        key={`drawer-${link.label}-${link.url}`}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={`${drawerRowClass} py-2`}
                        style={{ ...drawerRowStyle, paddingLeft: '40px', fontSize: '13px' }}
                      >
                        <span className="truncate">{link.label}</span>
                        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>↗</span>
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 메뉴 검색. 메뉴가 많아지면 스크롤로 찾기 어려우므로 위에 고정해 둔다(스크롤되는 것은 목록뿐). */}
        <div className="px-2 pt-3 pb-1 flex-shrink-0">
          <input
            className="input-field"
            data-tour="sidebar-search"
            style={{ fontSize: '13px', padding: '6px 10px' }}
            placeholder="메뉴 검색"
            value={menuQuery}
            onChange={(event) => setMenuQuery(event.target.value)}
            aria-label="메뉴 검색"
          />
        </div>

        {/*
          스크롤은 사이드바 전체가 아니라 메뉴 목록만 한다. 전체가 스크롤되면 아래 "위젯 관리" 가
          메뉴를 따라 위로 밀려 올라가 화면 밖으로 나간다 — 메뉴가 많거나 그룹을 펼쳤을 때 그렇다.
        */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5" data-tour="sidebar-menu">
          {searchResults === null ? (
            <>
              {/*
                홈 — 메뉴 목록의 <b>맨 첫 줄</b>. 상표(CI)를 누르면 홈으로 간다는 것을 모르는 사람이 있어
                눈에 보이는 길을 하나 낸다(사용자 지시 2026-08-31).

                <p>메뉴 트리(`menu_settings`)가 아니라 여기에 손으로 둔다 — 트리에 넣으면 권한·오픈
                게이트에 걸려 <b>누군가에게는 홈이 사라진다.</b> 홈은 로그인하면 누구나 처음 닿는 곳이라
                가려질 자리가 아니다. 순서 설정에서 아래로 밀리지 않아야 한다는 뜻이기도 하다.

                <p>이름은 「대시보드」가 아니라 「홈」이다 — 앱 전체가 그렇게 부르고(`screens.tsx`),
                헤더의 「관리자 대시보드」와 헷갈리지 않는다.
              */}
              <Link
                to={HOME_PATH}
                onClick={close}
                data-tour="sidebar-home"
                className="flex items-center gap-2.5 py-2 rounded-lg text-sm transition-all"
                style={{
                  paddingLeft: '12px',
                  background: pathname === HOME_PATH ? '#eff6ff' : 'transparent',
                  color: pathname === HOME_PATH ? 'var(--accent)' : 'var(--text-secondary)',
                  fontWeight: pathname === HOME_PATH ? 600 : 400,
                }}
              >
                <MenuIcon name="home" />
                <span>홈</span>
              </Link>

              {/*
                즐겨찾기 칸 — 홈 바로 아래, 본 메뉴 위 (V325). 담은 것이 없으면 <b>칸도 없다</b>:
                빈 제목만 서 있으면 사이드바 한 줄을 이유 없이 먹는다. 별을 한 번 누르면 나타난다.
              */}
              {favoriteItems.length > 0 && (
                <div className="space-y-0.5" style={{ paddingBottom: '4px', marginBottom: '4px', borderBottom: '1px solid var(--border)' }}>
                  <p style={{ padding: '6px 12px 2px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)' }}>
                    즐겨찾기
                  </p>
                  {favoriteItems.map(({ item, href, trail }, index) => (
                    <FavoriteRow
                      key={item.menuKey}
                      item={item}
                      href={href}
                      trail={trail}
                      active={isActive(item, pathname)}
                      first={index === 0}
                      last={index === favoriteItems.length - 1}
                      onOpen={close}
                      onUp={() => moveFavorite(item.menuKey, -1)}
                      onDown={() => moveFavorite(item.menuKey, 1)}
                      onRemove={() => favoriteActions.toggle.mutate({ menuKey: item.menuKey, favorite: false })}
                    />
                  ))}
                </div>
              )}

              {topLevel.map((item) => renderItem(item))}
            </>
          ) : searchResults.length === 0 ? (
            <p className="px-3 py-4 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
              검색 결과가 없습니다.
            </p>
          ) : (
            searchResults.map((hit) =>
              hit.kind === 'menu' ? renderSearchResult(hit.item, hit.groupLabel) : renderFeatureResult(hit),
            )
          )}
        </nav>

        {/*
          위젯 관리. 사이드바 맨 아래에 붙는다 — `aside` 가 flex 열이고 위의 `nav` 가 flex-1 이라
          메뉴가 몇 개든 이 블록은 바닥에 남는다. 메뉴가 아니라 화면 설정이므로 메뉴 목록과
          선을 그어 구분한다.

          누르면 홈으로 보내면서 `?widgets=1` 을 달아 준다. 관리 결과를 곧바로 눈으로 봐야 하는데,
          다른 화면 위에 모달만 띄우면 무엇이 바뀌었는지 확인할 수 없다.
        */}
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <Link
            to="/home?widgets=1"
            onClick={close}
            data-tour="sidebar-widgets"
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-slate-50"
            style={{ color: 'var(--text-secondary)' }}
          >
            <MenuIcon name="grid" />
            위젯 관리
          </Link>
          {/*
            로그아웃 — <b>lg 미만에서만</b> 여기 선다(데스크톱은 마이페이지 안에 있다). 서랍 위쪽
            목록에서 「직원찾기」 옆에 두면 메뉴를 고르려다 눌린다. 되돌릴 수 없는 동작은 이동과
            섞지 않고 손이 잘 가지 않는 바닥에 둔다.
          */}
          {sessionUser && (
            <button
              onClick={handleLogout}
              disabled={logout.isPending}
              className={`lg:hidden ${drawerRowClass}`}
              style={{ ...drawerRowStyle, borderTop: '1px solid var(--border)' }}
            >
              <MenuIcon name="lock" />
              {logout.isPending ? '로그아웃 중…' : '로그아웃'}
            </button>
          )}
          <VersionLine onNavigate={close} />
        </div>
      </aside>
      )}

      {/* 하위 메뉴 판 — 사이드바 오른쪽 가장자리에 붙는다(데스크톱). 좁은 화면은 줄 아래 펼침이 맡는다. */}
      {showSidebar && !collapsed && flyoutItem && (
        <div
          data-flyout-panel
          className="hidden lg:block fixed left-52 top-14 bottom-0 w-56 z-[60] overflow-y-auto no-print bg-white shadow-lg py-2 px-2"
          style={{ borderRight: '1px solid var(--border)' }}
        >
          <p
            className="truncate"
            style={{ padding: '4px 12px 6px', fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}
          >
            {flyoutItem.label}
          </p>
          <div className="space-y-1">
            {childrenOf(flyoutItem.menuKey).map((child) => renderFlyoutNode(child))}
          </div>
        </div>
      )}

      {/*
        서랍 뒤의 어두운 막. <b>상단바까지 덮는다</b>(사용자 지적 2026-08-26) — 막이 상단바(z-40)
        아래에 있던 탓에 화면 맨 윗줄만 하얗게 남아, 서랍이 열렸는데도 뒤를 계속 쓸 수 있는 것처럼
        보였다. 서랍 자신은 막보다 위여야 하므로 함께 올린다(z-[60]).
      */}
      {showSidebar && menuOpen && <div className="fixed inset-0 bg-black/30 z-50 lg:hidden" onClick={close} />}

      {showDirectoryModal && (
        <DirectoryModal
          key={directoryPersonId ?? ''}
          initialPersonnelId={directoryPersonId}
          onClose={() => {
            setShowDirectoryModal(false);
            setDirectoryPersonId(null);
          }}
        />
      )}
    </>
  );
}
